import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import {
  ERROR_MESSAGE,
  formatErrorMessage,
} from "../../../constants/errorMessage";
import { PageRoutes } from "../../../constants/routes";
import { appActions } from "../../../ducks/app";
import { Client, clientActions } from "../../../ducks/client";
import { useNavigation } from "../../../hooks/useNavigation";
import { AppDispatch } from "../../../store";
import { formatDateTime } from "../../../utils/date";
import {
  validateClientName,
  validateDateRange,
  validateFutureDate,
  validateStandardId,
} from "../../../utils/validation";

export const useClientEdit = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { navigateToPage } = useNavigation();
  const { clientId } = useParams<{ clientId: string }>();

  const [client, setClient] = useState<Client | null>(null);

  useEffect(() => {
    if (client || !clientId) return;

    dispatch(appActions.showLoading());

    dispatch(
      clientActions.getClientDetails.started({
        customerId: clientId,
        onComplete: () => dispatch(appActions.hideLoading()),
        onSuccess: (data) => {
          const clientData = data.data;
          const client: Client = {
            id: clientData.id,
            name: clientData.name,
            parentId: clientData.parent_id,
            validPeriod: {
              from: new Date(clientData.valid_from),
              to: new Date(clientData.valid_to),
            },
            createdAt: new Date(clientData.created_at),
            createdBy: clientData.created_by,
          };
          setClient(client);
        },
        onError: () => {
          navigateToPage(PageRoutes.CLIENTS);
        },
      }),
    );
  }, []);

  const handleCancel = useCallback(() => {
    dispatch(
      appActions.showModal({
        title: "内容は保存されません。よろしいですか？",
        buttons: [
          {
            variant: "dark",
            text: "閉じる",
            onClick: () => {
              dispatch(appActions.hideModal());
            },
          },
          {
            variant: "filled",
            text: "保存しない",
            onClick: () => {
              dispatch(appActions.hideModal());
              navigateToPage(PageRoutes.CLIENTS);
            },
          },
        ],
      }),
    );
  }, []);

  const handleEditClient = useCallback(
    (
      clientId: string,
      parentId: string,
      clientName: string,
      validFrom: string,
      validTo: string,
      onError: (message: { [key: string]: string }) => void,
    ) => {
      const isValidClientId = validateStandardId(clientId);
      const isValidParentId = parentId ? validateStandardId(parentId) : true;
      const isValidClientName = validateClientName(clientName);
      const isValidateValidFrom = validateDateRange(validFrom, validTo);
      const isValidValidTo = validateFutureDate(validTo);

      const errorMessage: { [key: string]: string } = {};

      if (!isValidClientId) {
        errorMessage["clientId"] = formatErrorMessage(ERROR_MESSAGE.maxLength, {
          field: "顧客ID",
          max: "14",
        });
      }

      if (!isValidParentId) {
        errorMessage["parentId"] = formatErrorMessage(ERROR_MESSAGE.maxLength, {
          field: "親顧客ID",
          max: "14",
        });
      }

      if (!isValidClientName) {
        errorMessage["clientName"] = formatErrorMessage(
          ERROR_MESSAGE.maxLength,
          {
            field: "顧客名",
            max: "255",
          },
        );
      }

      if (!isValidateValidFrom) {
        errorMessage["validFrom"] = formatErrorMessage(
          ERROR_MESSAGE.validFromDate,
          {
            field: "終了日",
          },
        );
      }

      if (!isValidValidTo) {
        errorMessage["validTo"] = formatErrorMessage(
          ERROR_MESSAGE.validToDate,
          {
            field: "開始日",
          },
        );
      }

      if (Object.keys(errorMessage).length > 0) {
        onError(errorMessage); // Show error message
        return;
      } else {
        onError({}); // Clear error message
      }

      dispatch(appActions.showLoading());

      dispatch(
        clientActions.updateClient.started({
          customerId: clientId,
          parent_id: parentId,
          name: clientName,
          valid_from: formatDateTime(validFrom, false, "YYYY-MM-DDThh:mm:ssZ"),
          valid_to: formatDateTime(validTo, false, "YYYY-MM-DDThh:mm:ssZ"),
          onComplete: () => dispatch(appActions.hideLoading()),
          onSuccess: () => {
            dispatch(
              appActions.showNotification({
                type: "success",
                message: "顧客情報を更新しました",
              }),
            );
            navigateToPage(PageRoutes.CLIENTS);
          },
        }),
      );
    },
    [clientId],
  );

  return { client, handleCancel, handleEditClient };
};
